import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TeacherDocument } from '@td/shared';
import { del, get, patch, postForm } from '../lib/api';

const KEY = ['documents'];

export interface DocumentSearch {
  documents: TeacherDocument[];
  /** True while some documents' words have not been read yet. */
  stillReading: boolean;
}

/**
 * The filing cabinet, or what matches.
 *
 * The search runs on the server, because that is where the words are. Sending
 * a hundred documents' text to the browser so the browser could search it
 * would be slower and would put every document she owns into memory to find
 * one.
 *
 * Keyed by the term, so results are cached per search and going back to a
 * previous one is instant. The previous list is kept on screen while a new one
 * is fetched, or the page flashes empty at every keystroke.
 */
export function useDocuments(search = '') {
  const term = search.trim();

  return useQuery({
    queryKey: [...KEY, term],
    queryFn: () =>
      get<DocumentSearch>(`/documents${term ? `?q=${encodeURIComponent(term)}` : ''}`),
    placeholderData: (previous) => previous,
  });
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KEY });
}

/**
 * Where the file itself is.
 *
 * A plain URL rather than a fetched blob, because printing needs the browser
 * to load it as a document — and the session cookie goes with it like any
 * other same-origin request.
 */
export function documentUrl(doc: TeacherDocument): string {
  return documentUrlById(doc.id);
}

/**
 * The same URL from an id alone.
 *
 * Sub plans hold documents by id and never see the whole record, and a second
 * place writing this path out by hand is a second place to get it wrong — as
 * it was, briefly, missing the `/file` on the end and printing nothing.
 */
export function documentUrlById(id: string): string {
  return `/api/documents/doc/${id}/file`;
}

export function documentDownloadUrl(doc: TeacherDocument): string {
  return `/api/documents/doc/${doc.id}/file?download`;
}

export interface DocumentHtml {
  html: string;
  /** False when the file could not be read — an older .doc, usually. */
  converted: boolean;
}

/**
 * A Word document as HTML.
 *
 * Converted and sanitised on the server, and kept there — so this is fetched
 * once per document and then cached like anything else.
 */
export function useDocumentHtml(doc: { id: string; contentType: string } | null) {
  const isDocx =
    doc?.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  return useQuery({
    queryKey: ['document-html', doc?.id],
    queryFn: () => get<DocumentHtml>(`/documents/doc/${doc!.id}/html`),
    enabled: !!doc && isDocx,
    staleTime: Infinity,
  });
}

export function useUploadDocument() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ file, note }: { file: File; note?: string }) => {
      const body = new FormData();
      body.append('file', file);
      if (note) body.append('note', note);
      return postForm<TeacherDocument>('/documents', body);
    },
    onSuccess: refresh,
  });
}

export function useUpdateDocument() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      name?: string;
      note?: string;
      pinned?: boolean;
    }) => patch<TeacherDocument>(`/documents/doc/${id}`, input),
    onSuccess: refresh,
  });
}

export function useDeleteDocument() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => del(`/documents/doc/${id}`),
    onSuccess: refresh,
  });
}
